import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"

import { getSampleApplicationStatus } from "@/api"
import { EAffiliateCampaignType, ECampaignType } from "@/modules/helpers/enum"
import { useAddresses } from "@/modules/helpers/hooks"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { useApplyCampaign, useApplyCampaignMCQ } from "@/modules/helpers/hooks/use-apply-campaign"
import { useModalFlow } from "@/modules/helpers/hooks/use-modal-flow"
import { useMultipleSkuSelection } from "@/modules/helpers/hooks/use-multiple-sku-selection"
import { useSkuSelection } from "@/modules/helpers/hooks/use-sku-selection"
import { sendHaptic, sendOpenLink } from "@/modules/helpers/utils"
import {
  buildAddressInfo,
  EMPTY_ADDRESS,
  extractSaleProps,
  filterAvailableSaleProps,
  formatAddress,
  hasAddressChanged,
  isUserRestricted,
  needsGMVCheck,
  resolveButtonStatus,
  validateFormDataLegacy,
  ValidationErrors
} from "@/modules/helpers/utils/campaign-form"
import type { Address } from "@/modules/types"
import type {
  AddressInfo,
  ApplyCampaignFormProps,
  InfoApplyKOC
} from "@/modules/types/jobs/campaign-form"

export function useApplyCampaignForm({
  data,
  refetch,
  onApplySuccess,
  initialSampleRegistered,
  isFetching
}: ApplyCampaignFormProps) {
  const { user } = useCurrentUser()
  const queryClient = useQueryClient()

  const [isSheetVisible, setIsSheetVisible] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isSampleRegistered, setIsSampleRegistered] = useState<boolean | undefined>(
    initialSampleRegistered
  )
  const [kolSubmissionId, setKolSubmissionId] = useState<string | null>(null)
  const [isCheckingSample, setIsCheckingSample] = useState(false)
  const [hasPendingSample, setHasPendingSample] = useState(
    data?.kolSubmissionStatus === "PENDING_REGISTER_SAMPLE"
  )
  const [addressInfo, setAddressInfo] = useState<AddressInfo>(EMPTY_ADDRESS)
  const [addressEditInfo, setAddressEditInfo] = useState<AddressInfo>(EMPTY_ADDRESS)
  const [contentTypes, setContentTypes] = useState<EAffiliateCampaignType[]>([
    EAffiliateCampaignType.VIDEO
  ])
  const [totalClip, setTotalClip] = useState("")
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null)
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false)

  // Sync sample-related state when campaign changes or data arrives
  useEffect(() => {
    setHasPendingSample(data?.kolSubmissionStatus === "PENDING_REGISTER_SAMPLE")
    setIsSampleRegistered(initialSampleRegistered)
    setKolSubmissionId(null)
  }, [data?.id, data?.kolSubmissionStatus, initialSampleRegistered])

  const { data: addressesResponse, refetch: refetchAddresses } = useAddresses()
  const addresses = useMemo(() => addressesResponse?.data || [], [addressesResponse?.data])

  const productHasSkus = !!data?.products?.skus?.length
  const isMultipleSkuMode = !!data?.productCampaigns?.length

  // Single SKU selection
  const { saleProp, selectedSkuId, togglePropId, toggleMultiplePropId } = useSkuSelection({
    products: data?.products
  })

  // Multiple SKU selection
  const productCampaignSaleProps = useMemo(() => {
    return data?.productCampaigns?.map((item) => ({
      saleProps: Array.isArray(item?.saleProps)
        ? item?.saleProps
        : extractSaleProps(item?.skus || []),
      title: item?.title,
      thumbnail: item?.images?.[0] || "",
      id: item?.id?.toString()
    }))
  }, [data?.productCampaigns])

  const {
    selectedSkus,
    togglePropValue,
    isSelected,
    getSelectedSkusArray,
    isProductComplete,
    getProductSelections
  } = useMultipleSkuSelection()

  const saleProps = useMemo(() => {
    const rawSaleProps = Array.isArray(data?.products?.saleProps)
      ? data.products.saleProps
      : extractSaleProps(data?.products?.skus || [])

    // Filter to only show options that have corresponding SKUs
    return filterAvailableSaleProps(rawSaleProps, data?.products?.skus || [])
  }, [data?.products])

  const {
    modals,
    openModal,
    closeModal,
    closeAllModals,
    handleConnectSocialCompleted,
    handleGMVCheckingCompleted,
    handleUpdateAddress,
    handleAddressUpdated,
    handleAddressModalClose
  } = useModalFlow(user, data?.isNeedGmv)

  const { mutate: applyCampaign, isPending: isApplying } = useApplyCampaign({
    campaignId: data?.id || "",
    type: ECampaignType.AFFILIATE,
    link: data?.products?.link,
    onMutationSuccess: (submissionId) => {
      // Close UI
      setIsSheetVisible(false)
      closeAllModals()

      // Refresh data
      refetch()
      onApplySuccess?.()
      queryClient.invalidateQueries({ queryKey: ["campaign"] })

      // Track submission
      if (submissionId) {
        setKolSubmissionId(submissionId)
        setHasPendingSample(true)
      }
    },
    onCompleted: (params) => {
      if (params?.kolSubmissionId) {
        setKolSubmissionId(params.kolSubmissionId)
      }
      if (params?.isRequestRegisterSample) {
        setIsSampleRegistered(true)
        setShowSuccessModal(true)
        setIsSheetVisible(false)
        queryClient.invalidateQueries({ queryKey: ["explore"] })
      }
    }
  })

  const { mutate: applyCampaignMCQ, isPending: isApplyingMCQ } = useApplyCampaignMCQ({
    campaignId: data?.id || "",
    type: ECampaignType.AFFILIATE,
    onCompleted: () => {
      setShowSuccessModal(true)
      setIsSheetVisible(false)
      closeAllModals()
      refetch()
      onApplySuccess?.()
      queryClient.invalidateQueries({ queryKey: ["campaign"] })
    }
  })

  useEffect(() => {
    // Initialize address from addresses list if available
    if (addresses.length > 0 && !selectedAddress) {
      const defaultAddress = addresses.find((addr: Address) => addr.isDefault) || addresses[0]
      if (defaultAddress) {
        const initialAddressInfo = buildAddressInfo(defaultAddress)
        setAddressInfo(initialAddressInfo)
        setAddressEditInfo(initialAddressInfo)
        setSelectedAddress(defaultAddress)
      }
    }
  }, [addresses, selectedAddress])

  const handleApply = useCallback(() => {
    const validation = validateFormDataLegacy({
      addressInfo,
      selectedAddress,
      contentTypes,
      totalClip,
      productHasSkus,
      selectedSkuId,
      isMultipleSkuMode,
      selectedSkus
    })

    if (!validation.isValid) {
      toast.error(
        validation.errors.address ||
          validation.errors.fullName ||
          validation.errors.phoneNumber ||
          validation.errors.contentTypes ||
          validation.errors.totalClip ||
          validation.errors.selectedSkuId
      )
      setValidationErrors(validation.errors)
      sendHaptic()
      return
    }

    setValidationErrors({})

    const addressData = selectedAddress
      ? {
          phoneNumber: selectedAddress.phoneNumber,
          fullName: selectedAddress.fullName,
          address: formatAddress(selectedAddress)
        }
      : {
          phoneNumber: addressInfo.phoneNumber,
          fullName: addressInfo.fullName,
          address: formatAddress(addressInfo as Address)
        }

    // Multiple SKU mode: different payload structure
    if (isMultipleSkuMode) {
      // Get all selected prop value IDs across all products
      const salePropIds = Object.values(selectedSkus)
        .flatMap((productSelections) => Object.values(productSelections))
        .filter(Boolean)

      // Find SKUs from productCampaigns that match selected prop values
      const skus: { skuId: string; productId: string }[] = []

      data?.productCampaigns?.forEach((productCampaign) => {
        if (productCampaign?.skus) {
          const matchedSku = productCampaign.skus.find((sku: any) =>
            sku?.skuSaleProps?.some((saleProp: any) =>
              salePropIds.includes(saleProp.propValueId?.toString())
            )
          )

          if (matchedSku) {
            skus.push({
              skuId: matchedSku.id?.toString() || "",
              productId: matchedSku.productId?.toString() || productCampaign.id?.toString() || ""
            })
          }
        }
      })

      if (skus.length === 0) {
        setValidationErrors({ selectedSkuId: "Vui lòng chọn ít nhất một phân loại sản phẩm" })
        toast.error("Vui lòng chọn ít nhất một phân loại sản phẩm")
        sendHaptic()
        return
      }

      applyCampaignMCQ({
        ...addressData,
        infoApplyKOC: {
          skus,
          contentTypes,
          totalClip
        }
      })
      return
    }

    const infoApplyKOC: InfoApplyKOC = {
      contentTypes,
      totalClip,
      ...(productHasSkus && selectedSkuId ? { skuId: selectedSkuId } : {})
    }

    applyCampaign({
      ...addressData,
      productId: data?.products?.id,
      infoApplyKOC
    })
  }, [
    addressInfo,
    selectedAddress,
    contentTypes,
    totalClip,
    productHasSkus,
    selectedSkuId,
    isMultipleSkuMode,
    selectedSkus,
    data?.products?.id,
    data?.productCampaigns,
    applyCampaign,
    applyCampaignMCQ
  ])

  const handleSelectSku = useCallback(
    (data: { skuId?: string; contentType: EAffiliateCampaignType[]; totalClip: string }) => {
      setContentTypes(data.contentType)
      setTotalClip(data.totalClip)
      closeModal("selectSku")

      if (hasAddressChanged(user, addressInfo)) {
        setTimeout(() => openModal("updateAddress"), 300)
      } else {
        handleApply()
      }
    },
    [user, addressInfo, closeModal, openModal, handleApply]
  )

  const handleAddressCompleted = useCallback(
    (newAddressInfo: AddressInfo) => {
      setAddressInfo(newAddressInfo)
      setAddressEditInfo(newAddressInfo)
      handleAddressUpdated()
      handleApply()
    },
    [handleAddressUpdated, handleApply]
  )

  const handleOpenSheet = useCallback(() => {
    if (!user?.tiktoks?.tiktokId) {
      openModal("validateChannel")
      return
    }

    if (needsGMVCheck(user, data?.isNeedGmv)) {
      openModal("gmvChecking")
      return
    }

    setIsSheetVisible(true)
    openModal("selectSku")
  }, [user, data?.isNeedGmv, openModal])

  const handleCloseSheet = useCallback(() => {
    setIsSheetVisible(false)
    closeAllModals()
    setValidationErrors({})
  }, [closeAllModals])

  const buttonStatus = useMemo(() => {
    return resolveButtonStatus({
      status: data?.status,
      isApplied: data?.isApplied,
      isRestricted: (data as any)?.isRestricted || false,
      isAccountRestricted: isUserRestricted(user, data?.rejectedReasons),
      requireShowcaseAuthorization: (data as any)?.requireShowcaseAuthorization || false,
      kolSubmissionStatus: data?.kolSubmissionStatus
    })
  }, [data, user])

  const handleSelectAddress = useCallback((address: Address) => {
    setSelectedAddress(address)
    const addressInfoFromSelected = buildAddressInfo(address)
    setAddressInfo(addressInfoFromSelected)
    setAddressEditInfo(addressInfoFromSelected)
  }, [])

  const handleCreateNewAddress = useCallback(() => {
    setIsAddingNewAddress(true)
    setAddressEditInfo(EMPTY_ADDRESS)
    setSelectedAddress(null)
  }, [])

  const handleEditAddress = useCallback(
    (address: Address) => {
      setIsAddingNewAddress(false)
      setSelectedAddress(address)
      const addressInfoFromSelected = buildAddressInfo(address)
      setAddressEditInfo(addressInfoFromSelected)
      openModal("updateAddress")
    },
    [openModal]
  )

  const handleConnectTikTok = useCallback(() => {
    sendOpenLink("https://www.tiktok.com/")
  }, [])

  // Additional handlers for component compatibility
  const toggleContentType = useCallback((type: EAffiliateCampaignType) => {
    setContentTypes((prev) => {
      if (prev.includes(type)) {
        const next = prev.filter((item) => item !== type)
        if (!next.length) return prev
        setValidationErrors((errors) => ({ ...errors, contentTypes: undefined }))
        return next
      }
      setValidationErrors((errors) => ({ ...errors, contentTypes: undefined }))
      return [...prev, type]
    })
  }, [])

  const updateTotalClip = useCallback((value: string) => {
    setTotalClip(value)
    if (value) {
      setValidationErrors((errors) => ({ ...errors, totalClip: undefined }))
    }
  }, [])

  const handleConfirm = useCallback(() => {
    sendHaptic()
    handleApply()
  }, [handleApply])

  const clearAddressErrors = useCallback(() => {
    setValidationErrors((prev) => ({
      ...prev,
      address: undefined,
      fullName: undefined,
      phoneNumber: undefined
    }))
  }, [])

  const handleAddressChange = useCallback(
    (address: Address | null) => {
      setSelectedAddress(address)
      setAddressInfo(buildAddressInfo(address))
      clearAddressErrors()
    },
    [clearAddressErrors]
  )

  const handleAddNewAddress = useCallback(() => {
    setIsAddingNewAddress(true)
    setAddressEditInfo(EMPTY_ADDRESS)
    openModal("updateAddress")
  }, [openModal])

  const handleAddressUpdateCompleted = useCallback(
    (info: AddressInfo) => {
      setAddressInfo(info)
      clearAddressErrors()
      closeModal("updateAddress")
    },
    [setAddressInfo, clearAddressErrors, closeModal]
  )

  const productImage = data?.products?.images?.[0] || ""

  const sampleErrorToast = useCallback(() => {
    toast.error("Hệ thống không tìm thấy yêu cầu mẫu của bạn. Vui lòng yêu cầu mẫu lại", {
      richColors: true,
      className: "bg-red-500 text-white text-sm"
    })
  }, [])

  const handleRequestSample = useCallback(async (): Promise<boolean> => {
    setIsCheckingSample(true)

    const submissionId = kolSubmissionId || data?.kolSubmissionId

    if (!submissionId) {
      setIsCheckingSample(false)
      sampleErrorToast()
      return false
    }

    try {
      const res = await getSampleApplicationStatus(submissionId)
      const isRegistered = res?.data?.data?.isRequestRegisterSample ?? false
      if (isRegistered) {
        setIsSampleRegistered(true)
        setShowSuccessModal(true)
        setIsSheetVisible(false)
        queryClient.invalidateQueries({ queryKey: ["explore"] })
      } else {
        sampleErrorToast()
      }
      return isRegistered
    } catch {
      sampleErrorToast()
      return false
    } finally {
      setIsCheckingSample(false)
    }
  }, [kolSubmissionId, data?.kolSubmissionId, queryClient, sampleErrorToast])

  return {
    // State
    isSheetVisible,
    setIsSheetVisible,
    showSuccessModal,
    setShowSuccessModal,
    addressInfo,
    setAddressInfo,
    addressEditInfo,
    setAddressEditInfo,
    contentTypes,
    setContentTypes,
    totalClip,
    setTotalClip,
    validationErrors,
    selectedAddress,
    setSelectedAddress,
    isAddingNewAddress,
    setIsAddingNewAddress,
    addresses,
    saleProps,
    productCampaignSaleProps,
    saleProp,
    selectedSkuId,
    togglePropId,
    toggleMultiplePropId,
    productHasSkus,
    buttonStatus,
    isPending: isApplying || isApplyingMCQ,
    productImage,
    user,

    // Multiple SKU selection state & handlers
    selectedSkus,
    togglePropValue,
    isSelected,
    getSelectedSkusArray,
    isProductComplete,
    getProductSelections,

    // Sample request
    isSampleRegistered,
    isCheckingSample,
    handleRequestSample,
    hasKolSubmissionId: !!(kolSubmissionId || data?.kolSubmissionId),
    hasPendingSample,
    isFetching,

    // Modals
    modals,
    openModal,
    closeModal,
    closeAllModals,
    handleConnectSocialCompleted,
    handleGMVCheckingCompleted,
    handleUpdateAddress,
    handleAddressUpdated,
    handleAddressModalClose,

    // Handlers
    handleApply,
    handleConfirm,
    handleSelectSku,
    handleAddressCompleted,
    handleOpenSheet,
    handleCloseSheet,
    handleAddressChange,
    handleSelectAddress,
    handleAddNewAddress,
    handleCreateNewAddress,
    handleAddressUpdateCompleted,
    handleEditAddress,
    toggleContentType,
    updateTotalClip,
    handleConnectTikTok,
    refetchAddresses
  }
}
